import * as React from 'react';
import { Lieferung } from 'api/lieferung';
import { LieferungPosition, changeLieferungPositionMenge } from 'api/lieferungPosition';
import { LieferArt } from 'formComponents/lookups/LieferArt';
import { TextField } from '@fluentui/react/lib/TextField';
import { formatDecimal, parseDecimal } from 'utils/formatter';
import { formStyles } from 'styles/formStyles';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { mergeStyleSets } from '@fluentui/react/lib/Styling';
import { useArtikelDetail } from 'api/artikelDetail';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { artikelGruppenByNrSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { useAuftragPositionList } from 'api/auftragPosition';

const styles = mergeStyleSets({
    changeMengeGrid: {
        marginTop: 20,
        marginBottom: 20,
        display: 'grid',
        gridTemplateColumns: '120px 120px 120px 120px',
        gridRowGap: 10,
        gridColumnGap: 10,
    },
    mengeLabel: {
        marginTop: 6,
    },
    menge: {
        textAlign: 'right',
    },
});

interface ChangeMenge {
    error: string | null;
    newMenge: number;
    newMengeS: string;
    newDifferenceS: string;
    newLArt: string;
}

const defaultChangeMenge: ChangeMenge = {
    error: null,
    newMenge: 0,
    newMengeS: '0',
    newDifferenceS: '0',
    newLArt: '',
};

interface LieferungPositionChangeMengeProps {
    lieferung: Lieferung;
    lieferungPosition: LieferungPosition;
    onClose: (refetch: boolean) => void;
}

export const LieferungPositionChangeMenge: React.FC<LieferungPositionChangeMengeProps> = ({
    lieferung,
    lieferungPosition,
    onClose,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const [changeMenge, setChangeMenge] = React.useState({
        ...defaultChangeMenge,
        newMenge: lieferungPosition.posMenge,
        newMengeS: formatDecimal(lieferungPosition.posMenge, 3),
        newDifferenceS: formatDecimal(0, 3),
        newLArt: lieferungPosition.lArt,
    });
    const [auftragMenge, setAuftragMenge] = React.useState({ posMenge: 0, offen: 0 });

    const auftragPositionList = useAuftragPositionList(
        {
            auNr: lieferung.auNr,
        },
        {
            enabled: !!lieferung.auNr,
        },
    );

    const artikelDetail = useArtikelDetail({
        artikelGruppeNr: lieferungPosition.artikelGruppeNr,
        artikelNr: lieferungPosition.artikelNr,
        includeArchiv: true,
    });

    React.useEffect(() => {
        if (lieferung.auNr && lieferungPosition && auftragPositionList.data) {
            let posMenge = 0;
            let offen = 0;
            auftragPositionList.data
                .filter(
                    (ap) =>
                        ap.artikelGruppeNr === lieferungPosition.artikelGruppeNr &&
                        ap.artikelNr === lieferungPosition.artikelNr,
                )
                .forEach((ap) => {
                    posMenge += ap.posMenge;
                    offen += ap.offen;
                });
            setAuftragMenge({ posMenge, offen });
        }
    }, [lieferung.auNr, lieferungPosition, lieferungPosition.pos, auftragPositionList]);

    const setValidatedChangeMengeLArt = (newMenge: number, newLArt: string) => {
        let error: string | null = null;
        if (newMenge < 0) {
            error = 'Menge darf nicht < 0 sein.';
        }

        setChangeMenge({
            error,
            newMenge,
            newMengeS: formatDecimal(newMenge, 3),
            newDifferenceS: formatDecimal(newMenge - (lieferungPosition?.posMenge || 0), 3),
            newLArt,
        });
    };

    return (
        <ConfirmMessage
            title="Lieferart / Menge ändern"
            message={`Lieferart / Menge des Artikels ${
                artikelGruppenByNr[lieferungPosition?.artikelGruppeNr]?.text
            } | ${lieferungPosition?.artikelNr} ändern?`}
            confirmText="Lieferart / Menge ändern"
            cancelText="Abbruch"
            minWidth={550}
            content={
                <>
                    <ul>
                        <li>Die offene Menge am Auftrag wird korrigiert.</li>
                        <li>
                            Bei Lieferart <strong>Lager</strong> wird <strong>Offene Lieferungen</strong> in der
                            Lagerverwaltung wird angepasst.
                        </li>
                        <li>
                            Bei Lieferart <strong>Lager</strong> wird der Bestand in der Lagerverwaltung angepasst.
                        </li>
                    </ul>
                    {auftragPositionList.isLoading || artikelDetail.isLoading ? (
                        <LoadingIndicator />
                    ) : (
                        <div className={styles.changeMengeGrid}>
                            <TextField
                                label="Gesamtmenge am Auftrag"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(auftragMenge.posMenge, 3)}
                            />
                            <TextField
                                label="Offene Menge am Auftrag"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(auftragMenge.offen, 3)}
                            />
                            <div />
                            <div />

                            <TextField
                                label="Lagerbestand"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.bestand || 0, 3)}
                            />
                            <TextField
                                label="offene Lief."
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenLief || 0, 3)}
                            />
                            <TextField
                                label="offene Best."
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenBest || 0, 3)}
                            />
                            <TextField
                                label="Reservierungen"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(artikelDetail.data?.offenRes || 0, 3)}
                            />

                            <div>
                                <LieferArt
                                    label="Aktuelle Lieferart"
                                    disabled
                                    allowEmpty
                                    value={lieferungPosition?.lArt || ''}
                                />
                            </div>
                            <div>
                                <LieferArt
                                    label="Neue Lieferart"
                                    allowEmpty
                                    value={changeMenge.newLArt || ''}
                                    menuPlacement="top"
                                    onChange={(newValue) => {
                                        setValidatedChangeMengeLArt(changeMenge.newMenge, newValue);
                                    }}
                                />
                            </div>
                            <div />
                            <div />

                            <TextField
                                label="Aktuelle Menge"
                                inputClassName={styles.menge}
                                readOnly
                                disabled
                                value={formatDecimal(lieferungPosition?.posMenge || 0, 3)}
                            />
                            <TextField
                                label="Neue Menge"
                                inputClassName={styles.menge}
                                value={changeMenge.newMengeS}
                                onChange={(event, newValue) =>
                                    setChangeMenge({
                                        ...changeMenge,
                                        newMengeS: newValue === undefined ? '' : newValue,
                                    })
                                }
                                onBlur={(event): void => {
                                    setValidatedChangeMengeLArt(
                                        parseDecimal(event.target.value) || 0,
                                        changeMenge.newLArt,
                                    );
                                }}
                            />
                            <div>
                                <TextField
                                    label="Differenz"
                                    inputClassName={styles.menge}
                                    value={changeMenge.newDifferenceS}
                                    onChange={(event, newValue) =>
                                        setChangeMenge({
                                            ...changeMenge,
                                            newDifferenceS: newValue === undefined ? '' : newValue,
                                        })
                                    }
                                    onBlur={(event): void => {
                                        setValidatedChangeMengeLArt(
                                            (lieferungPosition?.posMenge || 0) +
                                                (parseDecimal(event.target.value) || 0),
                                            changeMenge.newLArt,
                                        );
                                    }}
                                />
                            </div>
                            <div />
                        </div>
                    )}
                    {changeMenge.error && <div className={formStyles.error}>{changeMenge.error}</div>}
                </>
            }
            onConfirm={async () => {
                if (lieferungPosition) {
                    await changeLieferungPositionMenge({
                        ...lieferungPosition,
                        posMenge: changeMenge.newMenge,
                        lArt: changeMenge.newLArt,
                    }).then(() => {
                        onClose(true);
                        setChangeMenge(defaultChangeMenge);
                    });
                }
            }}
            confirmDisabled={
                !!changeMenge.error ||
                (changeMenge.newMenge === lieferungPosition?.posMenge &&
                    changeMenge.newLArt === lieferungPosition?.lArt)
            }
            onCancel={() => onClose(false)}
        />
    );
};
